/**
 * Nem mentett szerkesztés elhagyása: a `dirty-form` mixint használó űrlapok itt jelentkeznek be, és a router-őr
 * innen kérdez, mielőtt a felhasználó másik lapra lép (menüpont, link, vissza gomb). Egy űrlap akkor számít,
 * ha a modellje eltér a betöltöttől ÉS a felhasználó gépelt vagy választott benne: a betöltéskor magától
 * átrendeződő modell így nem kérdez.
 */
const forms = new Set();
let savedAt = 0;
let leaving = false;
let failedWrites = 0;

// egy mentés után a lap azonnal navigál (vissza a listára); a pillanatkép frissítése addig még nem futhatott le
const SAVE_GRACE_MS = 3000;

export function registerForm(form) {
    forms.add(form);
}

export function unregisterForm(form) {
    forms.delete(form);
}

// az írást az az űrlap indította, amelyikben a felhasználó utoljára kattintott vagy billentyűt nyomott (pl. a Mentés
// gombja); egy űrlapon kívüli kattintás (másik fül mentése) egyiké sem
const WRITE_ORIGIN_MS = 2000;
let lastInteraction = { form: null, at: 0 };

function noteInteraction(event) {
    if (!event.isTrusted) {
        return;
    }

    const form = [...forms].find(candidate => candidate.$el?.contains?.(event.target)) || null;
    lastInteraction = { form, at: Date.now() };
}

['pointerdown', 'keydown'].forEach(type => window.addEventListener(type, noteInteraction, true));

/** Az íráskor (a kérés indulásakor) kérdezzük: melyik űrlapé a mentés. Háttérkérésnél egyiké sem. */
export function writeOrigin() {
    return Date.now() - lastInteraction.at < WRITE_ORIGIN_MS ? lastInteraction.form : null;
}

/**
 * Sikeres írás (mentés) történt: a közvetlenül utána induló navigáció nem kérdez, és a mentő űrlap a mentett
 * állapotot veszi alapnak. Csak az, amelyik mentett: egy másik fül mentése vagy egy háttérkérés a többi űrlap
 * nem mentett változását nem nyelheti el.
 */
export function noteSaved(origin = null) {
    savedAt = Date.now();

    if (origin && forms.has(origin)) {
        origin.capturePristine?.();
    }
}

/** Elutasított írás (validációs vagy szerverhiba): a „Mentés és bezárás" ebből tudja, hogy nem zárhat be. */
export function noteWriteFailed() {
    failedWrites++;
}

export function writeFailures() {
    return failedWrites;
}

/** A felhasználó már megerősítette az elvetést (pl. a Bezárás gombon): a navigáció végéig nem kérdezünk újra. */
export function allowLeave() {
    leaving = true;
}

/** A navigáció lezárult (a router `afterEach`-e): a következő elhagyás újra kérdez. */
export function leaveFinished() {
    leaving = false;
}

export function hasUnsavedChanges() {
    return [...forms].some(form => form.unsavedChanges);
}

/** Igaz, ha a navigáció mehet: nincs nem mentett változás, vagy a felhasználó vállalja az elvetést. */
export async function confirmLeave() {
    if (leaving || Date.now() - savedAt < SAVE_GRACE_MS || !hasUnsavedChanges()) {
        return true;
    }

    // a router már induláskor betölti ezt a fájlt: a fordító (és rajta át a store) itt még körbeérne
    const [{ default: i18n }, { confirmDialog }] = await Promise.all([import('@/translations/i18n'), import('@/utils/confirm')]);
    const t = (key) => i18n.global.t(key);

    leaving = await confirmDialog({
        title: t('base.leave_confirm_title'),
        text: t('base.leave_confirm_message'),
        confirmText: t('base.leave').ucFirst(),
        cancelText: t('base.stay').ucFirst(),
        danger: true,
    });

    return leaving;
}

// a lap bezárása vagy újratöltése: a böngésző saját kérdése (a szövegét nem lehet megadni)
window.addEventListener('beforeunload', (event) => {
    if (hasUnsavedChanges()) {
        event.preventDefault();
        event.returnValue = '';
    }
});
